import {
  KoButton,
  KoDrawer,
  KoForm,
  KoLoader,
  KoModal,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoToast,
  KoEmpty
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { ENVIRONMENT_TYPE_CONFIG } from '../constants';
import EnvironmentCrud from './crud/EnvironmentCrud';
import EnvironmentVariableCrud from './crud/EnvironmentVariableCrud';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_ENVIRONMENTS = [
  { _id: 'env-001', name: 'Development', type: 'development', baseUrl: 'http://localhost:3000/api', description: 'Local development environment', variablesCount: 12, owner: 'Platform Team', updatedAt: '2026-02-24T10:00:00Z' },
  { _id: 'env-002', name: 'Staging', type: 'staging', baseUrl: 'https://staging-api.kwapio.com', description: 'Pre-production staging environment', variablesCount: 15, owner: 'DevOps Team', updatedAt: '2026-02-23T14:30:00Z' },
  { _id: 'env-003', name: 'Production', type: 'production', baseUrl: 'https://api.kwapio.com', description: 'Live production environment', variablesCount: 18, owner: 'DevOps Team', updatedAt: '2026-02-22T09:00:00Z' },
  { _id: 'env-004', name: 'QA Sandbox', type: 'custom', baseUrl: 'https://qa-api.kwapio.com', description: 'Isolated QA testing environment', variablesCount: 10, owner: 'QA Team', updatedAt: '2026-02-21T16:00:00Z' }
];

const MOCK_VARIABLES = [
  { _id: 'var-001', key: 'API_KEY', value: 'sk_test_***************', isSecret: true, description: 'Primary API key' },
  { _id: 'var-002', key: 'BASE_URL', value: 'https://api.kwapio.com', isSecret: false, description: 'API base URL' },
  { _id: 'var-003', key: 'DB_HOST', value: 'db-cluster.kwapio.internal', isSecret: false, description: 'Database cluster hostname' },
  { _id: 'var-004', key: 'REDIS_URL', value: 'redis://cache.kwapio.internal:6379', isSecret: false, description: 'Redis cache connection' },
  { _id: 'var-005', key: 'JWT_SECRET', value: '***************', isSecret: true, description: 'JWT signing secret' },
  { _id: 'var-006', key: 'SMTP_HOST', value: 'smtp.sendgrid.net', isSecret: false, description: 'Email SMTP server' },
  { _id: 'var-007', key: 'S3_BUCKET', value: 'kwapio-assets-prod', isSecret: false, description: 'S3 storage bucket' }
];

function Environments() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [environments, setEnvironments] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const [isEnvModalOpen, setEnvModalOpen] = useState(false);
  const [isVarModalOpen, setVarModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [variables, setVariables] = useState([]);

  const [envForm] = KoForm.useForm();
  const [varForm] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Environments' }
    ]));
    fetchEnvironments();
  }, [workspaceId]);

  const fetchEnvironments = () => {
    setLoading(true);
    ApiPlatformAPI.getEnvironments({ limit: 50 }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_ENVIRONMENTS;
        setEnvironments(data);
        setLoading(false);
      },
      () => { setEnvironments(MOCK_ENVIRONMENTS); setLoading(false); }
    );
  };

  const handleEnvSave = (values) => {
    setLoading(true);
    ApiPlatformAPI.createEnvironment(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setEnvModalOpen(false);
        envForm.resetFields();
        setCurrentRecord(null);
        fetchEnvironments();
        KoToast.success(currentRecord ? 'Environment updated.' : 'Environment created.');
      },
      () => { setLoading(false); KoToast.error('Failed to save environment.'); }
    );
  };

  const handleEnvDelete = () => {
    setLoading(true);
    ApiPlatformAPI.deleteEnvironment(currentRecord._id || currentRecord.environmentId).then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        fetchEnvironments();
        KoToast.success('Environment deleted.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const openDetail = (env) => {
    setSelectedEnv(env);
    setDrawerOpen(true);
    fetchVariables(env._id || env.environmentId);
  };

  const fetchVariables = (envId) => {
    ApiPlatformAPI.getEnvironmentVariables(envId).then(
      (res) => { const data = res?.data?.length ? res.data : MOCK_VARIABLES; setVariables(data); },
      () => setVariables(MOCK_VARIABLES)
    );
  };

  const handleVarSave = (values) => {
    setLoading(true);
    ApiPlatformAPI.setEnvironmentVariable(selectedEnv._id || selectedEnv.environmentId, values).then(
      () => {
        setLoading(false);
        setVarModalOpen(false);
        varForm.resetFields();
        fetchVariables(selectedEnv._id || selectedEnv.environmentId);
        KoToast.success('Variable saved.');
      },
      () => { setLoading(false); KoToast.error('Failed to save variable.'); }
    );
  };

  const handleVarDelete = (varId) => {
    ApiPlatformAPI.deleteEnvironmentVariable(selectedEnv._id || selectedEnv.environmentId, varId).then(
      () => {
        fetchVariables(selectedEnv._id || selectedEnv.environmentId);
        KoToast.success('Variable deleted.');
      },
      () => KoToast.error('Failed to delete variable.')
    );
  };

  const variableColumns = [
    { label: 'Key', id: 'key', key: 'key', width: 180,
      render: (val) => <span className={styles.variableKey}>{val}</span> },
    { label: 'Value', id: 'value', key: 'value', width: 240,
      render: (val, record) => (
        <span className={`${styles.variableValue} ${record.isSecret ? styles.variableMasked : ''}`}>
          {record.isSecret ? '••••••••' : (val || '—')}
        </span>
      )
    },
    { label: 'Description', id: 'description', key: 'description', width: 200 },
    { label: 'Secret', id: 'isSecret', key: 'isSecret', width: 70,
      render: (val) => val ? <KoIcon name='LockOutlined' width={14} height={14} /> : null },
    { label: '', id: 'actions', key: 'actions', width: 60,
      render: (_, record) => (
        <KoButton
          iconBtn
          danger
          icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
          onClick={() => handleVarDelete(record._id || record.variableId)}
        />
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='environments' />

      <KoToolBar
        title='Environments'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'New Environment',
          type: 'primary',
          icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
          onClick: () => { setCurrentRecord(null); envForm.resetFields(); setEnvModalOpen(true); }
        }]}
      />

      {environments.length === 0 && !isLoading && <KoEmpty description='No environments configured' />}

      <div className={styles.environmentCards}>
        {environments.map((env) => {
          const typeCfg = ENVIRONMENT_TYPE_CONFIG[env.type] || {};
          return (
            <div key={env._id || env.environmentId} className={styles.envCard} onClick={() => openDetail(env)}>
              <div className={styles.envCardHeader}>
                <span className={styles.envCardName}>{env.name}</span>
                <KoTag color={typeCfg.color}>{typeCfg.label || env.type}</KoTag>
              </div>
              {env.baseUrl && <div className={styles.envCardUrl}>{env.baseUrl}</div>}
              <div className={styles.envCardMeta}>
                <span>{env.variablesCount || 0} variables</span>
                {env.owner && <span>{env.owner}</span>}
              </div>
              <div className={styles.envCardActions}>
                <KoButton
                  label='Edit'
                  size='small'
                  onClick={(e) => { e.stopPropagation(); setCurrentRecord(env); envForm.setFieldsValue(env); setEnvModalOpen(true); }}
                />
                <KoButton
                  label='Delete'
                  size='small'
                  danger
                  onClick={(e) => { e.stopPropagation(); setCurrentRecord(env); setDeleteOpen(true); }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Environment Detail Drawer */}
      <KoDrawer
        title='Environment Detail'
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedEnv(null); setVariables([]); }}
        width={'min(700px, 95vw)'}
      >
        {selectedEnv && (
          <div>
            <div className={styles.drawerHeader}>
              <KoTitle level={4} text={selectedEnv.name} />
              <KoTag color={ENVIRONMENT_TYPE_CONFIG[selectedEnv.type]?.color}>
                {ENVIRONMENT_TYPE_CONFIG[selectedEnv.type]?.label || selectedEnv.type}
              </KoTag>
            </div>

            <div className={styles.detailMetaGrid}>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Base URL</span>
                <span className={styles.monoPath}>{selectedEnv.baseUrl || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Owner</span>
                <span className={styles.detailValue}>{selectedEnv.owner || '—'}</span>
              </div>
            </div>

            {selectedEnv.description && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Description</div>
                <KoText text={selectedEnv.description} />
              </div>
            )}

            <div className={styles.detailSection}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
                <div className={styles.detailLabel} style={{ marginBottom: 0 }}>Variables ({variables.length})</div>
                <KoButton
                  size='small'
                  type='primary'
                  label='Add Variable'
                  icon={<KoIcon name='PlusOutlined' width={12} height={12} color='#FFF' />}
                  onClick={() => { varForm.resetFields(); setVarModalOpen(true); }}
                />
              </div>
              {variables.length > 0 ? (
                <KoTable dataSource={variables} columns={variableColumns} rowKey={(r) => r._id || r.variableId || r.key} maxHeight={360} />
              ) : (
                <KoEmpty description='No variables configured' />
              )}
            </div>
          </div>
        )}
      </KoDrawer>

      {/* Env Create/Edit Modal */}
      <KoModal
        title={currentRecord ? 'Edit Environment' : 'New Environment'}
        centered
        open={isEnvModalOpen}
        handleOk={() => setEnvModalOpen(false)}
        onClose={() => { setEnvModalOpen(false); setCurrentRecord(null); }}
        width={'min(560px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setEnvModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => envForm.submit()} />
        ]}
      >
        <EnvironmentCrud form={envForm} handleFinish={handleEnvSave} />
      </KoModal>

      {/* Variable Create Modal */}
      <KoModal
        title='Add Variable'
        centered
        open={isVarModalOpen}
        handleOk={() => setVarModalOpen(false)}
        onClose={() => setVarModalOpen(false)}
        width={'min(560px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setVarModalOpen(false)} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => varForm.submit()} />
        ]}
      >
        <EnvironmentVariableCrud form={varForm} handleFinish={handleVarSave} />
      </KoModal>

      {/* Delete Confirmation */}
      <KoModal
        title='Delete Environment'
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleEnvDelete} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.name}"?`} />
          <KoText text='This will also remove all associated variables.' />
        </div>
      </KoModal>
    </div>
  );
}

export default Environments;
